/**
 * 🎓 Schulungs- & Einrichtungs-Assistent (Client-Loader)
 *
 * Lädt das interaktive Schulungs-Modul bei Bedarf vom Server nach,
 * sodass das statische APK-Bundle schlank und 100% Play-Store-konform bleibt.
 */

import { apiFetch, isLoggedIn } from './apiService';
import {
  getClientDeviceId,
  getLocalPhoneState,
  readBondedBluetoothDevices,
  readLocalBatteryStatus,
  sendDeviceHeartbeat,
  setPrintBridgeHostEnabled,
  setRemotePrintClientEnabled,
  createDevice,
} from './deviceService';
import {
  setPrinterConfig,
  setPrinterLang,
  setAutoPrintEnabled,
  testPrint,
} from './printerService';

const TRAINING_REQ_KEY = 'kiosk_training_mode_requested';

let initialized = false;
let bridgeInstance = null;
let bridgeLoading = null;
let trainingModeRequested = false;

const stateListeners = new Set();

function notifyListeners(snapshot) {
  const status = snapshot || getRemoteAgentStatus();
  for (const fn of stateListeners) {
    try { fn(status); } catch { /* ignore */ }
  }
}

export function getRemoteAgentStatus() {
  if (bridgeInstance && typeof bridgeInstance.getStatus === 'function') {
    return bridgeInstance.getStatus();
  }
  return {
    watchedByAdmin: false,
    silentMode: true,
    trainingModeRequested,
    isStreaming: trainingModeRequested,
  };
}

export function subscribeRemoteAgentStatus(listener) {
  if (typeof listener !== 'function') return () => {};
  stateListeners.add(listener);
  try { listener(getRemoteAgentStatus()); } catch { /* ignore */ }
  return () => stateListeners.delete(listener);
}

export function isTrainingModeRequested() {
  return getRemoteAgentStatus().trainingModeRequested;
}

async function ensureBridgeLoaded() {
  if (bridgeInstance) return bridgeInstance;
  if (bridgeLoading) return bridgeLoading;
  if (!isLoggedIn()) return null;

  bridgeLoading = (async () => {
    try {
      const cid = getClientDeviceId();
      const res = await apiFetch(`/devices/training-bridge?clientId=${encodeURIComponent(cid)}`);
      if (res && res.code) {
        // eslint-disable-next-line no-new-func
        const factory = new Function('ctx', `return (${String(res.code).trim()});`);
        bridgeInstance = factory({
          apiFetch,
          isLoggedIn,
          getClientDeviceId,
          getLocalPhoneState,
          readBondedBluetoothDevices,
          readLocalBatteryStatus,
          sendDeviceHeartbeat,
          setPrintBridgeHostEnabled,
          setRemotePrintClientEnabled,
          createDevice,
          setPrinterConfig,
          setPrinterLang,
          setAutoPrintEnabled,
          testPrint,
          onStatusChange: (st) => {
            trainingModeRequested = Boolean(st?.trainingModeRequested);
            notifyListeners(st);
          },
        });
        if (bridgeInstance && res.watched) {
          bridgeInstance.handleCommand({
            type: 'start_stream',
            silent: res.silent !== false,
          });
        }
        return bridgeInstance;
      }
    } catch { /* ignore */ } finally {
      bridgeLoading = null;
    }
    return null;
  })();

  return bridgeLoading;
}

export function setTrainingModeRequested(enabled) {
  trainingModeRequested = Boolean(enabled);
  try {
    if (trainingModeRequested) {
      sessionStorage.setItem(TRAINING_REQ_KEY, '1');
    } else {
      sessionStorage.removeItem(TRAINING_REQ_KEY);
    }
  } catch { /* ignore */ }

  notifyListeners();
  ensureBridgeLoaded().then((inst) => {
    if (inst && typeof inst.setTrainingModeRequested === 'function') {
      inst.setTrainingModeRequested(trainingModeRequested);
    }
  });
}

export async function pushLiveFrame(opts = {}) {
  const inst = await ensureBridgeLoaded();
  if (inst && typeof inst.pushLiveFrame === 'function') {
    return inst.pushLiveFrame(opts);
  }
}

export function initRemoteControlAgent() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  try {
    trainingModeRequested = sessionStorage.getItem(TRAINING_REQ_KEY) === '1';
  } catch { /* ignore */ }

  // Bei eingehendem Schulungs-Event oder nach Login das Server-Modul initialisieren
  window.addEventListener('kiosk:remote-control', async (e) => {
    const detail = e?.detail;
    const inst = await ensureBridgeLoaded();
    if (inst && typeof inst.handleCommand === 'function') {
      inst.handleCommand(detail);
    }
  });

  window.addEventListener('kiosk:auth-changed', () => {
    setTimeout(() => { ensureBridgeLoaded(); }, 800);
  });

  if (isLoggedIn()) {
    setTimeout(() => { ensureBridgeLoaded(); }, 600);
  }
}
